import React from 'react';
import { defineMessages } from 'react-intl-next';
import type { IntlShape } from 'react-intl-next';

import {
	SmartCreateFeedback,
	type SmartCreateFeedbackProps,
	SmartCreateFeedbackVariant,
} from '@atlassian/smart-create-feedback';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import type { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';

const i18n = defineMessages({
	createWithRovoModalCloseFeedbackFlagTitle: {
		id: 'smart-create.create-with-rovo-modal-close-feedback-flag.title',
		defaultMessage: 'Help us improve Create with Rovo',
		description: 'Title for the Rovo feedback flag',
	},
	createWithRovoModalCloseFeedbackFlagDescription: {
		id: 'smart-create.create-with-rovo-modal-close-feedback-flag.description',
		defaultMessage: "We'd love your feedback. This quick survey should take less than a minute.",
		description: 'Description for the Rovo feedback flag',
	},
	createWithRovoModalCloseFeedbackFlagSurveyButton: {
		id: 'smart-create.create-with-rovo-modal-close-feedback-flag.survey-button',
		defaultMessage: 'Share feedback',
		description: 'Label for the share feedback button',
	},
	createWithRovoUpdateCompleteFeedbackFlagTitle: {
		id: 'smart-create.create-with-rovo-update-complete-feedback-flag.title',
		defaultMessage: 'Help us improve Rovo',
		description:
			'Title for the Rovo feedback flag that displays after Rovo finishes updating an existing page for the user',
	},
	createWithRovoUpdateCompleteFeedbackFlagDescription: {
		id: 'smart-create.create-with-rovo-update-complete-feedback-flag.description',
		defaultMessage: "We'd love your feedback. This quick survey should take less than a minute.",
		description: 'Description for the Rovo update complete feedback flag',
	},
	createWithRovoUpdateCompleteFeedbackFlagSurveyButton: {
		id: 'smart-create.create-with-rovo-update-complete-feedback-flag.survey-button',
		defaultMessage: 'Share feedback',
		description: 'Label for the share feedback button',
	},
});

// This is needed because useDialogs expects an onCloseComplete callback, but SmartCreateFeedback does not have one.
const SmartCreateFeedbackDialog = ({
	onClose,
	onCloseComplete,
	...props
}: SmartCreateFeedbackProps & { onCloseComplete: () => void }) => {
	return (
		<SmartCreateFeedback
			onClose={() => {
				onClose();
				onCloseComplete();
			}}
			{...props}
		/>
	);
};
export const SmartCreateFeedbackDialog_ForTesting = SmartCreateFeedbackDialog;

export enum FeedbackVariant {
	AFTER_AIFC_MODAL_CLOSE = 'after-aifc-modal-close',
	AFTER_UPDATE_STREAM_COMPLETE = 'after-update-stream-complete',
}

const getFeedbackConfig = (
	feedbackVariant: FeedbackVariant,
	contentType?: 'page' | 'whiteboard',
) => {
	switch (feedbackVariant) {
		case FeedbackVariant.AFTER_AIFC_MODAL_CLOSE:
			return {
				flagId: 'create-with-rovo-modal-close-feedback-flag',
				localStorageKey: keys.CREATE_WITH_ROVO_MODAL_CLOSE_FEEDBACK_FLAG_LAST_SHOWN,
				titleI18n: i18n.createWithRovoModalCloseFeedbackFlagTitle,
				descriptionI18n: i18n.createWithRovoModalCloseFeedbackFlagDescription,
				feedbackButtonI18n: i18n.createWithRovoModalCloseFeedbackFlagSurveyButton,
				cooldownMs: 30 * 24 * 60 * 60 * 1000, // 30 days
				flagDisplayProbability: 1.0,
				extraDialogProps: {
					variant: SmartCreateFeedbackVariant.CREATE_WITH_ROVO,
					additionalFields: {
						'Entry Point': 'Confluence after AIFC modal close flag',
						contentType,
					},
				},
			};
		case FeedbackVariant.AFTER_UPDATE_STREAM_COMPLETE:
			return {
				flagId: 'create-with-rovo-update-complete-feedback-flag',
				localStorageKey: keys.CREATE_WITH_ROVO_UPDATE_COMPLETE_FEEDBACK_FLAG_LAST_SHOWN,
				titleI18n: i18n.createWithRovoUpdateCompleteFeedbackFlagTitle,
				descriptionI18n: i18n.createWithRovoUpdateCompleteFeedbackFlagDescription,
				feedbackButtonI18n: i18n.createWithRovoUpdateCompleteFeedbackFlagSurveyButton,
				cooldownMs: 90 * 24 * 60 * 60 * 1000, // 90 days
				flagDisplayProbability: 0.15,
				extraDialogProps: {
					variant: SmartCreateFeedbackVariant.ROVO,
					additionalFields: {
						'Entry Point': 'Confluence after update streaming complete flag',
						contentType,
					},
				},
			};
	}
};

export const showCreateWithRovoModalCloseFeedbackFlag = (
	flags: FlagsStateContainer,
	intl: IntlShape,
	showModal: ReturnType<typeof useDialogs>['showModal'],
	feedbackVariant: FeedbackVariant,
	contentType?: 'page' | 'whiteboard',
) => {
	const config = getFeedbackConfig(feedbackVariant, contentType);

	if (
		!isInCooldownPeriod(config.localStorageKey, config.cooldownMs) &&
		passesProbabilityCheck(config.flagDisplayProbability)
	) {
		flags.showFlag({
			id: config.flagId,
			title: intl.formatMessage(config.titleI18n),
			description: intl.formatMessage(config.descriptionI18n),
			type: 'success',
			actions: [
				{
					content: intl.formatMessage(config.feedbackButtonI18n),
					onClick: () => {
						flags.hideFlag(config.flagId);
						showModal(SmartCreateFeedbackDialog, {
							onClose: () => {},
							onSubmit: () => {},
							...config.extraDialogProps,
						});
					},
				},
			],
		});
		void localStorage.setItem(config.localStorageKey, Date.now().toString());
	}
};

const isInCooldownPeriod = (localStorageKey: string, cooldownMs: number) => {
	try {
		const lastShown = localStorage.getItem(localStorageKey);
		if (!lastShown) {
			return false;
		}
		return Date.now() - parseInt(lastShown) < cooldownMs;
	} catch (error) {
		return false;
	}
};

export const passesProbabilityCheck = (probability: number) => {
	return Math.random() < probability;
};
